import { useQuery } from "@tanstack/react-query";
import { Mail, MailCheck, MailWarning, Lock } from "lucide-react";
import { api, MOVEMENT_LABELS, MOVEMENT_COLORS, fmtDateTime, locationLabel } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";

const PHOTO_LABELS = { frontal: "Frontal", trasera: "Trasera", lateral_izquierdo: "Lateral Izq.", lateral_derecho: "Lateral Der." };

function EmailStatus({ status }) {
  if (status === "enviado") return <span className="flex items-center gap-1 text-emerald-600 text-xs" data-testid="email-status-enviado"><MailCheck className="w-3.5 h-3.5" /> Correo enviado</span>;
  if (status === "fallido") return <span className="flex items-center gap-1 text-red-600 text-xs" data-testid="email-status-fallido"><MailWarning className="w-3.5 h-3.5" /> Correo fallido</span>;
  return <span className="flex items-center gap-1 text-slate-400 text-xs" data-testid="email-status-pendiente"><Mail className="w-3.5 h-3.5" /> Enviando...</span>;
}

export function MovementDetailDialog({ movementId, open, onClose }) {
  const { data: m } = useQuery({
    queryKey: ["movement-detail", movementId],
    queryFn: async () => (await api.get(`/movements/${movementId}`)).data,
    enabled: open && !!movementId,
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="movement-detail-dialog">
        {m && (
          <>
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2 flex-wrap">
                <span className="font-mono tracking-wider">{m.container_number}</span>
                <Badge variant="outline" className={MOVEMENT_COLORS[m.movement_type]}>{MOVEMENT_LABELS[m.movement_type]}</Badge>
                {m.proceso_completado && (
                  <Badge className="bg-slate-800 text-white border-0" data-testid="detail-proceso-completado">
                    <Lock className="w-3 h-3 mr-1" /> Proceso Completado
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {[
                ["ID Movimiento", m.movement_id],
                ["Tamaño", m.size],
                ["Operación", `${m.operation_type} #${m.operation_number}`],
                ["Sello", m.seal_number || "-"],
                ["Estado", m.container_condition],
                ["Estatus del Proceso", m.process_status || "-"],
                ["Puerto", m.puerto || "-"],
                ["Released", m.released || "-"],
                ["Bill of Lading (B/L)", m.bl_number || "-"],
                ["Ubicación", locationLabel(m)],
                ["Llegada", fmtDateTime(m.arrival_datetime)],
                ["Salida", fmtDateTime(m.departure_datetime)],
                ["Registrado", fmtDateTime(m.created_at)],
                ["Camión", m.truck_matricula || "-"],
                ["Conductor", m.driver_nombre || "-"],
                ["Registrado por", m.created_by_name || "-"],
              ].map(([k, v]) => (
                <div key={k} className="bg-slate-50 rounded-lg px-3 py-2" data-testid={`detail-${k.toLowerCase().replace(/[\s/()]/g, "-")}`}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{k}</p>
                  <p className="font-semibold text-navy">{v}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {Object.entries(PHOTO_LABELS).map(([key, label]) =>
                m.photos?.[key] ? (
                  <figure key={key} className="space-y-1">
                    <img src={m.photos[key]} alt={label} className="w-full h-36 object-cover rounded-lg border border-slate-200" data-testid={`detail-photo-${key}`} />
                    <figcaption className="text-[10px] text-slate-400 text-center">{label}</figcaption>
                  </figure>
                ) : null
              )}
            </div>
            <EmailStatus status={m.email_status} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
